import { useEffect, useState } from 'react';
import { Outlet, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { LogOut, LayoutDashboard, FileText, Settings, ExternalLink, Menu, X, ChevronDown, PenSquare, List, Image, Users, ClipboardList, Package, ShoppingBag } from 'lucide-react';

const navItems = [
  {
    label: 'Gallery',
    icon: Image,
    href: '/admin/gallery',
  },
  {
    label: 'Artists',
    icon: Users,
    href: '/admin/artists',
  },
  {
    label: 'Products',
    icon: Package,
    href: '/admin/products',
  },
  {
    label: 'Orders',
    icon: ShoppingBag,
    href: '/admin/orders',
  },
  {
    label: 'Open Call',
    icon: ClipboardList,
    href: '/admin/open-call',
  },
  {
    label: 'Pages',
    icon: FileText,
    href: '/admin/pages',
  },
  {
    label: 'Blog',
    icon: LayoutDashboard,
    children: [
      { label: 'All Posts', icon: List, href: '/admin/blog' },
      { label: 'New Post', icon: PenSquare, href: '/admin/blog/new' },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    href: '/admin/settings',
  },
];

export default function AdminLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blogExpanded, setBlogExpanded] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setIsAuthenticated(api.isAuthenticated());
  }, [navigate]);

  // Auto-expand Blog section if on a blog route
  useEffect(() => {
    if (location.pathname.startsWith('/admin/blog')) {
      setBlogExpanded(true);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    api.logout();
    setIsAuthenticated(false);
    navigate('/admin/login');
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const isActive = (href: string) => location.pathname === href;
  const isBlogActive = location.pathname.startsWith('/admin/blog');

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-gray-200">
        <span className="text-lg font-bold text-gray-900 tracking-tight">GBA Admin</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          if (item.children) {
            return (
              <div key={item.label}>
                <button
                  onClick={() => setBlogExpanded(!blogExpanded)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isBlogActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <item.icon size={18} />
                    {item.label}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${blogExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
                {blogExpanded && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive(child.href)
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <child.icon size={16} />
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.href}
              to={item.href!}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href!)
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer actions */}
      <div className="px-3 py-4 border-t border-gray-200 space-y-1">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <ExternalLink size={18} />
          View Site
        </a>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-60 md:fixed md:inset-y-0 bg-white border-r border-gray-200 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`fixed inset-y-0 left-0 w-60 bg-white border-r border-gray-200 z-50 flex flex-col transform transition-transform duration-200 md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="absolute top-4 right-4">
          <button onClick={() => setSidebarOpen(false)} aria-label="Close sidebar" className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 md:ml-60 flex flex-col min-h-screen">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            className="text-gray-600 hover:text-gray-900"
          >
            <Menu size={22} />
          </button>
          <span className="text-lg font-bold text-gray-900">GBA Admin</span>
        </header>

        <main className="flex-1 px-4 sm:px-8 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}